import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  Select,
  formatSum,
  toTiyin,
  type Localized,
} from '@/components/ui';

/**
 * Рабочее место склада: очередь приёмки, форма проверки и закрытие возврата.
 * Суммы штрафов считает сервер — здесь показывается уже посчитанное.
 */

const RESULTS: Record<string, string> = {
  OK: 'Без замечаний',
  DAMAGED: 'Повреждение',
  LOST: 'Утрачена',
};

const RETURN_STATUS: Record<
  string,
  { label: string; tone: 'neutral' | 'green' | 'red' | 'amber' }
> = {
  PENDING: { label: 'Принят, не проверен', tone: 'amber' },
  IN_PROGRESS: { label: 'Проверяется', tone: 'amber' },
  OK: { label: 'Без замечаний', tone: 'green' },
  DAMAGED: { label: 'С повреждениями', tone: 'red' },
  CLOSED: { label: 'Закрыт', tone: 'neutral' },
};

interface QueueRow {
  id: string;
  status: string;
  returnedAt: string;
  inspectionDeadlineAt: string;
  overdue: boolean;
  checkedCount: number;
  order: { id: string; number: string; status: string; depositTotal: number };
}

interface DamageType {
  id: string;
  name: Localized;
  calcMode: string;
  value: number;
  isActive: boolean;
}

interface Settlement {
  claimed: number;
  withheld: number;
  refund: number;
  outstanding: number;
}

interface ReturnDetail {
  id: string;
  status: string;
  inspectionDeadlineAt: string;
  settlement: Settlement;
  inspections: Array<{
    id: string;
    orderItemId: string;
    result: string;
    notes: string | null;
    damages: Array<{ id: string; fineAmount: number; description: string | null }>;
  }>;
  order: {
    id: string;
    number: string;
    depositTotal: number;
    fineTotal: number;
    lateFeeTotal: number;
    items: Array<{
      id: string;
      snapshot: { name?: Localized };
      subtotal: number;
      depositAmount: number;
    }>;
  };
}

export default function ReturnsPage() {
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: ['returns-queue', overdueOnly],
    queryFn: async () =>
      (
        await api.get<QueueRow[]>('/admin/returns', {
          params: { overdue: overdueOnly ? 'true' : undefined },
        })
      ).data,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Возвраты</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Проверка идёт по позициям. Просроченный дедлайн — застрявший залог клиента
          </p>
        </div>
        <Button variant="secondary" onClick={() => setOverdueOnly((v) => !v)}>
          {overdueOnly ? 'Показать все' : 'Только просроченные'}
        </Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Card>
        {queue.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (queue.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-neutral-500">Очередь пуста</p>
        ) : (
          <ul className="space-y-1">
            {queue.data!.map((row) => (
              <li
                key={row.id}
                onClick={() => setOpenOrderId(row.order.id === openOrderId ? null : row.order.id)}
                className="flex cursor-pointer flex-wrap items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                <span className="font-medium">{row.order.number}</span>
                <Badge tone={RETURN_STATUS[row.status]?.tone ?? 'neutral'}>
                  {RETURN_STATUS[row.status]?.label ?? row.status}
                </Badge>
                <span className="text-neutral-500">проверено позиций: {row.checkedCount}</span>
                <span className={row.overdue ? 'text-red-600' : 'text-neutral-500'}>
                  до {new Date(row.inspectionDeadlineAt).toLocaleDateString('ru-RU')}
                </span>
                {row.overdue && <Badge tone="red">просрочено</Badge>}
                <span className="ml-auto">залог {formatSum(row.order.depositTotal)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {openOrderId && <ReturnCard orderId={openOrderId} onError={setError} />}
    </div>
  );
}

function ReturnCard({
  orderId,
  onError,
}: {
  orderId: string;
  onError: (message: string | null) => void;
}) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['return', orderId],
    queryFn: async () => (await api.get<ReturnDetail>(`/admin/returns/orders/${orderId}`)).data,
  });

  const damageTypes = useQuery({
    queryKey: ['damage-types'],
    queryFn: async () => (await api.get<DamageType[]>('/admin/damage-types')).data,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['return', orderId] });
    void queryClient.invalidateQueries({ queryKey: ['returns-queue'] });
  };

  const close = useMutation({
    mutationFn: async (returnId: string) => api.post(`/admin/returns/${returnId}/close`, {}),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: (e) => onError(errorMessage(e)),
  });

  if (detail.isLoading || !detail.data) {
    return (
      <Card title="Возврат">
        <p className="text-sm text-neutral-500">Загрузка…</p>
      </Card>
    );
  }

  const r = detail.data;
  const closed = r.status === 'CLOSED';

  return (
    <Card
      title={`Возврат по заказу ${r.order.number}`}
      actions={
        !closed && (
          <Button disabled={close.isPending} onClick={() => close.mutate(r.id)}>
            Закрыть возврат
          </Button>
        )
      }
    >
      <div className="grid gap-2 text-sm sm:grid-cols-4">
        <Metric label="Залог" value={formatSum(r.order.depositTotal)} />
        <Metric label="Штрафы" value={formatSum(r.order.fineTotal)} />
        <Metric label="Пеня" value={formatSum(r.order.lateFeeTotal)} />
        <Metric
          label={r.settlement.outstanding > 0 ? 'Долг сверх залога' : 'К возврату клиенту'}
          value={formatSum(
            r.settlement.outstanding > 0 ? r.settlement.outstanding : r.settlement.refund,
          )}
        />
      </div>

      <div className="mt-4 space-y-3">
        {r.order.items.map((item) => (
          <InspectionRow
            key={item.id}
            returnId={r.id}
            item={item}
            damageTypes={damageTypes.data ?? []}
            existing={r.inspections.find((i) => i.orderItemId === item.id)}
            disabled={closed}
            onSaved={() => {
              onError(null);
              invalidate();
            }}
            onError={(e) => onError(errorMessage(e))}
          />
        ))}
      </div>
    </Card>
  );
}

function InspectionRow({
  returnId,
  item,
  damageTypes,
  existing,
  disabled,
  onSaved,
  onError,
}: {
  returnId: string;
  item: ReturnDetail['order']['items'][number];
  damageTypes: DamageType[];
  existing?: ReturnDetail['inspections'][number];
  disabled: boolean;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const [result, setResult] = useState(existing?.result ?? 'OK');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [damageTypeId, setDamageTypeId] = useState('');
  const [manualAmount, setManualAmount] = useState('');

  const save = useMutation({
    mutationFn: async () =>
      api.post(`/admin/returns/${returnId}/inspect`, {
        orderItemId: item.id,
        result,
        notes: notes || undefined,
        damages:
          result === 'OK'
            ? []
            : [
                {
                  damageTypeId: damageTypeId || undefined,
                  manualAmount: manualAmount ? toTiyin(manualAmount) : undefined,
                  description: notes || undefined,
                },
              ],
      }),
    onSuccess: onSaved,
    onError,
  });

  const fine = existing?.damages.reduce((sum, d) => sum + d.fineAmount, 0) ?? 0;

  return (
    <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{item.snapshot.name?.ru ?? 'Позиция'}</span>
        <span className="text-xs text-neutral-500">
          аренда {formatSum(item.subtotal)} · залог {formatSum(item.depositAmount)}
        </span>
        {existing && (
          <Badge tone={existing.result === 'OK' ? 'green' : 'red'}>
            {RESULTS[existing.result] ?? existing.result}
            {fine > 0 && ` · ${formatSum(fine)}`}
          </Badge>
        )}
      </div>

      {!disabled && (
        <div className="mt-2 grid gap-2 sm:grid-cols-4">
          <Field label="Результат">
            <Select value={result} onChange={(e) => setResult(e.target.value)}>
              {Object.entries(RESULTS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>

          {result !== 'OK' && (
            <>
              <Field label="Тип повреждения">
                <Select value={damageTypeId} onChange={(e) => setDamageTypeId(e.target.value)}>
                  <option value="">Только ручная сумма</option>
                  {damageTypes
                    .filter((type) => type.isActive)
                    .map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name.ru}
                      </option>
                    ))}
                </Select>
              </Field>

              <Field label="Своя сумма, сум" hint="Перебивает справочник">
                <Input
                  value={manualAmount}
                  inputMode="numeric"
                  placeholder="По справочнику"
                  onChange={(e) => setManualAmount(e.target.value)}
                />
              </Field>
            </>
          )}

          <Field label="Заметка">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          <div className="sm:col-span-4">
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              {existing ? 'Переоценить' : 'Сохранить проверку'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-neutral-50 p-2 dark:bg-neutral-900">
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
